import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AppLinkCell, MatchAllButton } from "./OpenBankingAppLink";
import { 
  Loader2, 
  Building2, 
  Link2, 
  Copy, 
  RefreshCw, 
  Eye, 
  Trash2,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  Banknote,
  Search,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Download,
  Calendar
} from "lucide-react";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from "recharts";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

// Status badge styling
const getStatusBadge = (status) => {
  const statusConfig = {
    CR: { label: "Pending", variant: "outline", icon: Clock, color: "text-yellow-600" },
    GC: { label: "In Progress", variant: "outline", icon: Clock, color: "text-blue-600" },
    UA: { label: "Authenticating", variant: "outline", icon: Clock, color: "text-blue-600" },
    SA: { label: "Selecting", variant: "outline", icon: Clock, color: "text-blue-600" },
    GA: { label: "Granting", variant: "outline", icon: Clock, color: "text-blue-600" },
    LN: { label: "Connected", variant: "default", icon: CheckCircle, color: "text-green-600" },
    RJ: { label: "Rejected", variant: "destructive", icon: XCircle, color: "text-red-600" },
    SU: { label: "Suspended", variant: "outline", icon: AlertCircle, color: "text-orange-600" },
    EX: { label: "Expired", variant: "outline", icon: XCircle, color: "text-gray-600" },
  };
  
  const config = statusConfig[status] || { label: status, variant: "outline", icon: AlertCircle, color: "text-gray-600" };
  const Icon = config.icon;
  
  return (
    <Badge variant={config.variant} className={`${config.color} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
};

// Chart colors (kept for potential future use)
// const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

export default function OpenBanking() {
  const [activeTab, setActiveTab] = useState("customers");
  const [loading, setLoading] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [transactionData, setTransactionData] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [fetchingTransactions, setFetchingTransactions] = useState(false);
  
  // Create link form
  const [createForm, setCreateForm] = useState({
    institution_id: "",
    customer_name: "",
    business_name: "",
    sf_number: ""
  });
  const [createdLink, setCreatedLink] = useState(null);
  const [bankSearch, setBankSearch] = useState("");

  // Fetch institutions
  const fetchInstitutions = useCallback(async () => {
    try {
      const response = await api.get("/open-banking/institutions?country=GB");
      setInstitutions(response.data.institutions || []);
    } catch (error) {
      console.error("Failed to fetch institutions:", error);
      toast.error("Failed to load banks");
    }
  }, []);

  // Fetch requisitions
  const fetchRequisitions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/open-banking/requisitions");
      setRequisitions(response.data.requisitions || []);
    } catch (error) {
      console.error("Failed to fetch requisitions:", error);
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInstitutions();
    fetchRequisitions();
  }, [fetchInstitutions, fetchRequisitions]);

  // Create requisition
  const handleCreateLink = async () => {
    if (!createForm.institution_id || !createForm.customer_name || !createForm.business_name) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post("/open-banking/requisitions", createForm);
      setCreatedLink(response.data);
      toast.success("Link created successfully!");
      fetchRequisitions();
      setCreateForm({ institution_id: "", customer_name: "", business_name: "", sf_number: "" });
    } catch (error) {
      console.error("Failed to create link:", error);
      toast.error(errorDetailMessage(error, "Failed to create link"));
    } finally {
      setLoading(false);
    }
  };

  // Copy link to clipboard
  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard!");
  };

  // Refresh requisition status
  const refreshStatus = async (requisitionId) => {
    try {
      await api.get(`/open-banking/requisitions/${requisitionId}`);
      toast.success("Status refreshed");
      fetchRequisitions();
    } catch (error) {
      toast.error("Failed to refresh status");
    }
  };

  // Fetch transactions for a requisition
  const fetchTransactions = async (requisition) => {
    setSelectedRequisition(requisition);
    setFetchingTransactions(true);
    setShowTransactionModal(true);
    
    try {
      // First try to get stored data
      const storedResponse = await api.get(`/open-banking/requisitions/${requisition.requisition_id}/transactions`);
      
      if (storedResponse.data.has_data) {
        setTransactionData(storedResponse.data.transaction_data);
        setFetchingTransactions(false);
        return;
      }
      
      // If no stored data, fetch fresh
      const response = await api.post(`/open-banking/requisitions/${requisition.requisition_id}/fetch-transactions?days_back=90`);
      setTransactionData(response.data.transaction_data);
      toast.success("Transactions fetched successfully!");
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
      toast.error(errorDetailMessage(error, "Failed to fetch transactions"));
    } finally {
      setFetchingTransactions(false);
    }
  };

  // Force refresh transactions
  const forceRefreshTransactions = async () => {
    if (!selectedRequisition) return;
    
    setFetchingTransactions(true);
    try {
      const response = await api.post(`/open-banking/requisitions/${selectedRequisition.requisition_id}/fetch-transactions?days_back=90`);
      setTransactionData(response.data.transaction_data);
      toast.success("Transactions refreshed!");
    } catch (error) {
      toast.error(errorDetailMessage(error, "Failed to refresh transactions"));
    } finally {
      setFetchingTransactions(false);
    }
  };

  // ── Export current requisition to CSV ─────────────────────────────
  // Team asked for a one-click download so they don't have to
  // screenshot or copy-paste. Header block carries the customer +
  // account identifiers (sort code, account number, IBAN, product,
  // BIC, owner). Body has one row per transaction with columns:
  // Transaction date | Description | Credit | Debit | Balance —
  // exactly the shape the team already uses in their existing
  // statement templates.
  const exportTransactionsCSV = () => {
    if (!selectedRequisition || !transactionData) {
      toast.error("No transaction data to export");
      return;
    }

    const esc = (v) => {
      // RFC 4180 CSV quoting — wrap only if the value contains a comma,
      // double-quote, or newline; double up any embedded quotes.
      const s = (v ?? "").toString();
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const fmtMoney = (n) => (typeof n === "number" && !isNaN(n))
      ? n.toFixed(2)
      : "";

    const lines = [];

    // Helper to build a 2-column "Label,Value" row with proper escaping.
    const kv = (label, value) => `${esc(label)},${esc(value)}`;

    // ── Customer header ──────────────────────────────────────────
    lines.push(kv("Customer", selectedRequisition.customer_name || ""));
    lines.push(kv("Business", selectedRequisition.business_name || ""));
    lines.push(kv("Reference", selectedRequisition.reference || ""));
    lines.push(kv("Requisition ID", selectedRequisition.requisition_id || ""));
    lines.push(kv("Institution", selectedRequisition.institution_id || ""));
    lines.push(kv("Exported", new Date().toISOString()));
    const fetchedAt = transactionData.fetched_at || transactionData.analysis?.summary?.fetched_at;
    if (fetchedAt) lines.push(kv("Data fetched", fetchedAt));
    const summary = transactionData.analysis?.summary || {};
    if (summary.months_covered) lines.push(kv("Months covered", summary.months_covered));
    lines.push("");

    // ── Per-account header block (sort code, account number, IBAN) ───
    // UK BBAN convention: first 6 digits = sort code, remaining 8 = account no.
    (transactionData.accounts || []).forEach((acct, idx) => {
      if (acct.error) {
        lines.push(kv(`Account ${idx + 1}`, `ERROR — ${acct.error}`));
        lines.push("");
        return;
      }
      const details = acct.details?.account || {};
      const balances = (acct.balances?.balances || []).map(
        (b) => `${b.balanceType || "?"} ${b.balanceAmount?.currency || ""} ${b.balanceAmount?.amount || ""}`
      );
      // Derive UK sort code + account number. UK banks (Barclays,
      // NatWest, etc.) return this in the `scan` field (Sort Code +
      // Account Number, 14 digits). Older/EU banks use `bban`. Try
      // both — first 6 digits = sort code, last 8 = account number.
      let sortCode = "";
      let accountNumber = "";
      const scanOrBban = ((details.scan || details.bban || "") + "").replace(/\s+/g, "");
      if (scanOrBban && scanOrBban.length >= 14) {
        sortCode = `${scanOrBban.slice(0, 2)}-${scanOrBban.slice(2, 4)}-${scanOrBban.slice(4, 6)}`;
        accountNumber = scanOrBban.slice(6, 14);
      }
      // Map GC cashAccountType codes to human-readable product names.
      const cashTypeMap = {
        CACC: "Current Account",
        SVGS: "Savings Account",
        LOAN: "Loan Account",
        CARD: "Credit Card",
        MOMA: "Money Market",
        OTHR: "Other",
      };
      const product = details.product || cashTypeMap[details.cashAccountType] || details.cashAccountType || "";
      lines.push(kv(`Account ${idx + 1}`, details.ownerName || ""));
      if (product) lines.push(kv("Product", product));
      if (sortCode) lines.push(kv("Sort code", sortCode));
      if (accountNumber) lines.push(kv("Account number", accountNumber));
      if (details.iban) lines.push(kv("IBAN", details.iban));
      if (details.bic) lines.push(kv("BIC", details.bic));
      if (details.currency) lines.push(kv("Currency", details.currency));
      if (balances.length) lines.push(kv("Balances", balances.join(" | ")));
      lines.push("");
    });

    // ── Transactions table ───────────────────────────────────────
    // The analysis engine sorts all_transactions newest-first with a
    // pre-computed running balance. We reuse that so the CSV matches
    // exactly what ops sees on-screen.
    lines.push(["Transaction date", "Description", "Credit", "Debit", "Balance"].map(esc).join(","));
    const txns = transactionData.analysis?.all_transactions || [];
    txns.forEach((t) => {
      const amount = typeof t.amount === "number" ? t.amount : parseFloat(t.amount || 0);
      const credit = amount > 0 ? fmtMoney(amount) : "";
      const debit  = amount < 0 ? fmtMoney(Math.abs(amount)) : "";
      const desc   = t.description || t.creditor || t.debtor || "";
      lines.push([
        esc(t.date || ""),
        esc(desc),
        esc(credit),
        esc(debit),
        esc(fmtMoney(t.balance)),
      ].join(","));
    });

    // ── Download blob ────────────────────────────────────────────
    // Prepend UTF-8 BOM so Excel opens the file with the right
    // encoding (£ signs, accented names, etc.) without prompting.
    const csv = "\uFEFF" + lines.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // Filename: "OpenBanking - {business} - {yyyy-mm-dd}.csv"
    const safeName = (selectedRequisition.business_name || selectedRequisition.customer_name || "customer")
      .replace(/[/\\?%*:|"<>]/g, "-")
      .trim();
    const today = new Date().toISOString().slice(0, 10);
    a.download = `OpenBanking - ${safeName} - ${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${txns.length} transactions`);
  };

  // Delete requisition
  const deleteRequisition = async (requisitionId) => {
    if (!confirm("Are you sure you want to delete this connection?")) return;
    
    try {
      await api.delete(`/open-banking/requisitions/${requisitionId}`);
      toast.success("Connection deleted");
      fetchRequisitions();
    } catch (error) {
      toast.error("Failed to delete connection");
    }
  };

  // Filter institutions by search
  const filteredInstitutions = institutions.filter(inst => 
    inst.name.toLowerCase().includes(bankSearch.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Link2 className="w-6 h-6" />
            Open Banking
          </h1>
          <p className="text-muted-foreground">
            Connect customer bank accounts via GoCardless to fetch transaction data
          </p>
        </div>
        <Button onClick={fetchRequisitions} variant="outline" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="customers">Customers ({requisitions.length})</TabsTrigger>
          <TabsTrigger value="create">Create Link</TabsTrigger>
          <TabsTrigger value="banks">Available Banks ({institutions.length})</TabsTrigger>
        </TabsList>

        {/* Customers Tab */}
        <TabsContent value="customers" className="space-y-4">
          {requisitions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Link2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Bank Connections Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create a link to send to your customer to connect their bank account
                </p>
                <Button onClick={() => setActiveTab("create")}>
                  Create Link
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle>Connected Customers</CardTitle>
                    <CardDescription>
                      View and manage customer bank connections
                    </CardDescription>
                  </div>
                  <MatchAllButton onDone={fetchRequisitions} />
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Business</TableHead>
                      <TableHead>Application</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Last Fetched</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requisitions.map((req) => (
                      <TableRow key={req.id}>
                        <TableCell className="font-medium">{req.customer_name}</TableCell>
                        <TableCell>{req.business_name}</TableCell>
                        <TableCell><AppLinkCell req={req} onChanged={fetchRequisitions} /></TableCell>
                        <TableCell>{getStatusBadge(req.status)}</TableCell>
                        <TableCell>
                          {req.created_at ? new Date(req.created_at).toLocaleDateString("en-GB") : '-'}
                        </TableCell>
                        <TableCell>
                          {req.last_fetched_at ? new Date(req.last_fetched_at).toLocaleDateString("en-GB") : 'Never'}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          {req.link && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => copyLink(req.link)}
                              title="Copy Link"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => refreshStatus(req.requisition_id)}
                            title="Refresh Status"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                          {req.status === "LN" && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => fetchTransactions(req)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => deleteRequisition(req.requisition_id)}
                            className="text-red-600 hover:text-red-700"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Create Link Tab */}
        <TabsContent value="create" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Create Form */}
            <Card>
              <CardHeader>
                <CardTitle>Create Bank Connection Link</CardTitle>
                <CardDescription>
                  Generate a link to send to your customer for bank authorization
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Customer Name *</Label>
                  <Input 
                    placeholder="e.g., John Smith"
                    value={createForm.customer_name}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, customer_name: e.target.value }))}
                  />
                </div>
                
                <div>
                  <Label>Business Name *</Label>
                  <Input 
                    placeholder="e.g., ABC Trading Ltd"
                    value={createForm.business_name}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, business_name: e.target.value }))}
                  />
                </div>
                
                <div>
                  <Label>Application (SF number)</Label>
                  <Input 
                    placeholder="e.g., SF-36410 — optional, links the feed to the deal straight away"
                    value={createForm.sf_number}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, sf_number: e.target.value }))}
                    data-testid="ob-create-sf-input"
                  />
                </div>
                
                <div>
                  <Label>Select Bank *</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input 
                      placeholder="Search banks..."
                      value={bankSearch}
                      onChange={(e) => setBankSearch(e.target.value)}
                      className="pl-9 mb-2"
                    />
                  </div>
                  <Select 
                    value={createForm.institution_id}
                    onValueChange={(value) => setCreateForm(prev => ({ ...prev, institution_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a bank" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {filteredInstitutions.map((inst) => (
                        <SelectItem key={inst.id} value={inst.id}>
                          <div className="flex items-center gap-2">
                            {inst.logo && (
                              <img src={inst.logo} alt="" className="w-5 h-5 rounded" />
                            )}
                            {inst.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  onClick={handleCreateLink} 
                  className="w-full"
                  disabled={loading || !createForm.institution_id || !createForm.customer_name || !createForm.business_name}
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                  Generate Link
                </Button>
              </CardContent>
            </Card>

            {/* Created Link Display */}
            <Card>
              <CardHeader>
                <CardTitle>Generated Link</CardTitle>
                <CardDescription>
                  Copy this link and send it to your customer
                </CardDescription>
              </CardHeader>
              <CardContent>
                {createdLink ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 text-green-700 mb-2">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Link Created Successfully!</span>
                      </div>
                      <p className="text-sm text-green-600 mb-2">Reference: {createdLink.reference}</p>
                    </div>
                    
                    <div>
                      <Label className="text-sm text-muted-foreground">Authorization Link</Label>
                      <div className="flex gap-2 mt-1">
                        <Input 
                          value={createdLink.link || ""} 
                          readOnly 
                          className="font-mono text-xs"
                        />
                        <Button onClick={() => copyLink(createdLink.link)}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="text-sm text-muted-foreground">
                      <p>Send this link to your customer. When they click it:</p>
                      <ol className="list-decimal ml-4 mt-2 space-y-1">
                        <li>They will be redirected to their bank</li>
                        <li>They authorize access to their account data</li>
                        <li>They are redirected back to our portal</li>
                        <li>You can then view their transactions</li>
                      </ol>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Link2 className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p>Fill in the form and click "Generate Link" to create a bank connection link</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Banks Tab */}
        <TabsContent value="banks">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Available Banks</CardTitle>
                  <CardDescription>
                    Banks supported by GoCardless Open Banking
                  </CardDescription>
                </div>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search banks..."
                    value={bankSearch}
                    onChange={(e) => setBankSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[600px] overflow-y-auto">
                {filteredInstitutions.map((inst) => (
                  <div 
                    key={inst.id}
                    className="flex items-center gap-2 p-2 border rounded-lg hover:bg-gray-50"
                  >
                    {inst.logo ? (
                      <img src={inst.logo} alt="" className="w-8 h-8 rounded object-contain" />
                    ) : (
                      <Building2 className="w-8 h-8 text-muted-foreground" />
                    )}
                    <span className="text-sm truncate">{inst.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Transaction Modal */}
      <Dialog open={showTransactionModal} onOpenChange={setShowTransactionModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Banknote className="w-5 h-5" />
                Bank Account Analysis
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportTransactionsCSV}
                  disabled={fetchingTransactions || !transactionData}
                  data-testid="open-banking-export-csv"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button variant="outline" size="sm" onClick={forceRefreshTransactions} disabled={fetchingTransactions}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${fetchingTransactions ? 'animate-spin' : ''}`} />
                  Refresh Data
                </Button>
              </div>
            </DialogTitle>
            <DialogDescription>
              {selectedRequisition && (
                <span>
                  {selectedRequisition.customer_name} - {selectedRequisition.business_name}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {fetchingTransactions ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Fetching transaction data...</p>
                <p className="text-sm text-muted-foreground">This may take a moment</p>
              </div>
            </div>
          ) : transactionData ? (
            <TransactionDashboard data={transactionData} />
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>No transaction data available</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Transaction Dashboard Component (Equifax-like)
function TransactionDashboard({ data }) {
  const analysis = data?.analysis || {};
  const summary = analysis.summary || {};
  const regularOutgoings = analysis.regular_outgoings || [];
  const incomeSources = analysis.income_sources || [];
  const balanceHistory = analysis.balance_history || [];
  // Support both old "recent_transactions" and new "all_transactions" key
  const allTransactions = analysis.all_transactions || analysis.recent_transactions || [];

  // Filter state — sales team wanted:
  //   • default = last 30 days (so the modal isn't overwhelmed with 4500+ rows)
  //   • multi-select by month (checkbox list — "July ✓ June ✓ May ✗ April ✓")
  //   • "All time" toggle for the full dataset
  // `monthFilter` values:
  //   • "recent30"  — last 30 calendar days (default)
  //   • "all"       — every transaction
  //   • "months"    — respect selectedMonths (Set of "YYYY-MM" keys)
  const [monthFilter, setMonthFilter] = useState("recent30");
  const [selectedMonths, setSelectedMonths] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [showCount, setShowCount] = useState(100);

  // Build month options from actual transaction data
  const monthOptions = (() => {
    const months = new Map();
    allTransactions.forEach(txn => {
      if (!txn.date) return;
      const d = new Date(txn.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      if (!months.has(key)) months.set(key, label);
    });
    return Array.from(months.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  })();

  // Precompute the last-30-day cutoff (once per data render). Any txn on
  // or after this ISO date passes the "recent30" filter.
  const recent30Cutoff = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);  // YYYY-MM-DD
  })();

  // Filter transactions
  const filteredTransactions = allTransactions.filter(txn => {
    // Date-based filter
    if (monthFilter === "recent30") {
      if (!txn.date || txn.date < recent30Cutoff) return false;
    } else if (monthFilter === "months") {
      if (!txn.date) return false;
      const d = new Date(txn.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      // Empty selection = show nothing (safer than "show everything" which
      // would surprise the user who just unchecked their last month).
      if (!selectedMonths.has(key)) return false;
    }
    // monthFilter === "all" → no date restriction

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const desc = (txn.description || txn.creditor || txn.debtor || "").toLowerCase();
      const amountStr = String(txn.amount);
      if (!desc.includes(term) && !amountStr.includes(term)) return false;
    }
    return true;
  });

  // Compute filtered summary
  const filteredSummary = (() => {
    let totalIn = 0, totalOut = 0;
    filteredTransactions.forEach(txn => {
      if (txn.amount > 0) totalIn += txn.amount;
      else totalOut += Math.abs(txn.amount);
    });
    return {
      total_incoming: totalIn,
      total_outgoing: totalOut,
      net_flow: totalIn - totalOut,
      count: filteredTransactions.length
    };
  })();

  // Use filtered summary when a filter is active, otherwise use full summary
  const isFiltered = monthFilter !== "all" || searchTerm;
  const displaySummary = isFiltered ? {
    total_incoming: filteredSummary.total_incoming,
    total_outgoing: filteredSummary.total_outgoing,
    net_flow: filteredSummary.net_flow,
    transaction_count: filteredSummary.count,
    ending_balance: summary.ending_balance,
    avg_monthly_incoming: summary.avg_monthly_incoming,
    avg_monthly_outgoing: summary.avg_monthly_outgoing,
  } : summary;

  const visibleTransactions = filteredTransactions.slice(0, showCount);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Total In</p>
            <p className="text-lg font-bold text-green-600">{formatCurrency(displaySummary.total_incoming)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Total Out</p>
            <p className="text-lg font-bold text-red-600">{formatCurrency(displaySummary.total_outgoing)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Net Flow</p>
            <p className={`text-lg font-bold ${displaySummary.net_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(displaySummary.net_flow)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Ending Balance</p>
            <p className={`text-lg font-bold ${(summary.ending_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(summary.ending_balance)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Transactions</p>
            <p className="text-lg font-bold">{displaySummary.transaction_count}{isFiltered ? ` / ${allTransactions.length}` : ''}</p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Averages */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Avg Monthly Income ({summary.months_covered || 3} months)</p>
            <p className="text-lg font-bold text-green-600">{formatCurrency(summary.avg_monthly_incoming)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Avg Monthly Outgoing ({summary.months_covered || 3} months)</p>
            <p className="text-lg font-bold text-red-600">{formatCurrency(summary.avg_monthly_outgoing)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Balance History Chart */}
      {balanceHistory.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Balance History</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={balanceHistory}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  formatter={(value) => [formatCurrency(value), "Balance"]}
                  labelFormatter={(date) => new Date(date).toLocaleDateString("en-GB")}
                />
                <Line 
                  type="monotone" 
                  dataKey="balance" 
                  stroke="#2563eb" 
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Income Sources & Regular Outgoings side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              Top Income Sources
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
              {incomeSources.slice(0, 10).map((source, idx) => (
                <div key={idx} className="flex items-center justify-between py-1 border-b last:border-0">
                  <span className="text-sm truncate max-w-[60%]">{source.name}</span>
                  <div className="text-right">
                    <span className="text-sm font-medium text-green-600">{formatCurrency(source.total)}</span>
                    <span className="text-xs text-muted-foreground ml-1">({source.count}x)</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-red-600" />
              Regular Outgoings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
              {regularOutgoings.slice(0, 10).map((outgoing, idx) => (
                <div key={idx} className="flex items-center justify-between py-1 border-b last:border-0">
                  <span className="text-sm truncate max-w-[60%]">{outgoing.name}</span>
                  <div className="text-right">
                    <span className="text-sm font-medium text-red-600">{formatCurrency(outgoing.total)}</span>
                    <span className="text-xs text-muted-foreground ml-1">({outgoing.count}x)</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Full Transaction List with Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-base" data-testid="transactions-title">
              Transactions ({filteredTransactions.length}{isFiltered ? ` of ${allTransactions.length}` : ''})
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input 
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setShowCount(100); }}
                  className="pl-8 h-8 w-48 text-sm"
                  data-testid="transaction-search-input"
                />
              </div>

              {/* Preset toggles — "Last 30 days" is the default; ops
                  can switch to "All time" or open the month picker to
                  cherry-pick specific months (e.g. July+June+April,
                  skip May). */}
              <Button
                variant={monthFilter === "recent30" ? "default" : "outline"}
                size="sm"
                className="h-8 text-xs"
                onClick={() => { setMonthFilter("recent30"); setShowCount(100); }}
                data-testid="filter-recent30"
              >
                Last 30 days
              </Button>
              <Button
                variant={monthFilter === "all" ? "default" : "outline"}
                size="sm"
                className="h-8 text-xs"
                onClick={() => { setMonthFilter("all"); setShowCount(100); }}
                data-testid="filter-all-time"
              >
                All time
              </Button>

              {/* Month-picker popover — checkbox list, multi-select.
                  Selecting any month flips the filter mode to "months"
                  so preset toggles reflect the active state correctly. */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={monthFilter === "months" ? "default" : "outline"}
                    size="sm"
                    className="h-8 text-xs"
                    data-testid="filter-months-toggle"
                  >
                    <Calendar className="w-3.5 h-3.5 mr-1.5" />
                    {monthFilter === "months" && selectedMonths.size > 0
                      ? `${selectedMonths.size} month${selectedMonths.size === 1 ? "" : "s"}`
                      : "Pick months"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-56 p-2" data-testid="month-picker-popover">
                  <div className="flex items-center justify-between px-1 pb-2 mb-1 border-b">
                    <span className="text-xs font-semibold text-slate-700">Months</span>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        className="text-[10px] text-blue-600 hover:underline"
                        onClick={() => {
                          setSelectedMonths(new Set(monthOptions.map(([k]) => k)));
                          setMonthFilter("months");
                          setShowCount(100);
                        }}
                        data-testid="month-picker-all"
                      >
                        Select all
                      </button>
                      <span className="text-[10px] text-slate-300">|</span>
                      <button
                        type="button"
                        className="text-[10px] text-blue-600 hover:underline"
                        onClick={() => setSelectedMonths(new Set())}
                        data-testid="month-picker-none"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {monthOptions.length === 0 && (
                      <div className="text-xs text-slate-400 px-1 py-2">No months yet</div>
                    )}
                    {monthOptions.map(([key, label]) => {
                      const checked = selectedMonths.has(key);
                      return (
                        <label
                          key={key}
                          className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-slate-50 cursor-pointer text-sm"
                          data-testid={`month-picker-item-${key}`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              const next = new Set(selectedMonths);
                              if (v) next.add(key); else next.delete(key);
                              setSelectedMonths(next);
                              // Any interaction with the checkboxes engages
                              // the "months" filter mode so the presets
                              // deactivate correctly.
                              setMonthFilter("months");
                              setShowCount(100);
                            }}
                          />
                          <span>{label}</span>
                        </label>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>

              {(monthFilter !== "recent30" || searchTerm) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 text-xs"
                  onClick={() => {
                    setMonthFilter("recent30");
                    setSelectedMonths(new Set());
                    setSearchTerm("");
                    setShowCount(100);
                  }}
                  data-testid="clear-filters-btn"
                >
                  Reset
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="max-h-[500px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right w-[120px]">Amount</TableHead>
                  <TableHead className="text-right w-[120px]">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      No transactions found{isFiltered ? ' for this filter' : ''}
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleTransactions.map((txn, idx) => (
                    <TableRow key={idx} data-testid={`transaction-row-${idx}`}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {txn.date ? new Date(txn.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                      </TableCell>
                      <TableCell className="text-sm max-w-[350px] truncate">
                        {txn.description || txn.creditor || txn.debtor || '-'}
                      </TableCell>
                      <TableCell className={`text-right text-sm font-medium ${txn.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {txn.amount >= 0 ? '' : '-'}{formatCurrency(Math.abs(txn.amount))}
                      </TableCell>
                      <TableCell className={`text-right text-sm ${txn.balance >= 0 ? 'text-slate-700' : 'text-red-600'}`}>
                        {formatCurrency(txn.balance)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {filteredTransactions.length > showCount && (
            <div className="flex justify-center pt-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowCount(prev => prev + 100)}
                data-testid="load-more-transactions-btn"
              >
                Show More ({filteredTransactions.length - showCount} remaining)
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
